import React from 'react';

/**
 * Focuses the selected payment method once, on the transition into `isOpen`
 * (bos-seller#58: the method group has initial focus). A plain `autoFocus`
 * on the group loses the same race `VoidConfirmDialog`'s Confirm button does
 * — react-aria's own `useDialog` mount effect wins the same-tick flush; a
 * `setTimeout` deferred to the next macrotask reliably wins instead.
 *
 * Reaches into the rendered `RadioButtonGroup`'s DOM (a real grouped
 * `<input type="radio">` per item) rather than a documented focus API —
 * `@heroui-pro/react`'s `RadioButtonGroup` exposes no ref-forwarded focus
 * method for "focus the group" the way a collection component like
 * `ListBox` does.
 */
export const useFocusMethodGroup = (
    isOpen: boolean,
): React.RefObject<HTMLDivElement | null> => {
    const groupRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }
        const timeout = setTimeout(() => {
            groupRef.current
                ?.querySelector<HTMLInputElement>('input[type="radio"]:checked')
                ?.focus();
        }, 0);
        return () => clearTimeout(timeout);
    }, [isOpen]);

    return groupRef;
};
